/**
 * Pure CSV string builder. Deliberately has no DOM or file dependencies so it can be unit tested
 * in isolation and reused by non-browser code paths (e.g., a server-side export, or a "copy to
 * clipboard" flow that wants the same output as the file download).
 */

import {
  csvEscape,
  guardFormulaInjection,
  truncateForExcel
} from './csvEscape';
import type { BuildCsvOptions, ColumnDef } from './types';

const DEFAULTS: Required<BuildCsvOptions> = {
  lineEnding: '\r\n',
  delimiter: ',',
  excelSepHint: false,
  formulaGuard: true,
  bom: false,
  truncateLongCells: true
};

export function buildCsv<TRow>(
  rows: readonly TRow[],
  columns: readonly ColumnDef<TRow>[],
  options: BuildCsvOptions = {}
): string {
  if (columns.length === 0) {
    throw new Error('buildCsv: at least one column is required');
  }

  const opts: Required<BuildCsvOptions> = { ...DEFAULTS, ...options };
  const lines: string[] = [];

  if (opts.excelSepHint) {
    // `sep=<delimiter>` must be the very first line and is not quoted. Excel consumes it as a
    // directive; parsers that don't understand it treat it as an extra header row.
    lines.push(`sep=${opts.delimiter}`);
  }

  lines.push(
    columns
      .map(col => csvEscape(col.header, opts.delimiter))
      .join(opts.delimiter)
  );

  for (const row of rows) {
    const cells = columns.map(col => formatCell(row, col, opts));
    lines.push(cells.join(opts.delimiter));
  }

  const content = lines.join(opts.lineEnding);
  return opts.bom ? '\uFEFF' + content : content;
}

function formatCell<TRow>(
  row: TRow,
  column: ColumnDef<TRow>,
  opts: Required<BuildCsvOptions>
): string {
  const rawValue = column.value
    ? column.value(row)
    : (row as Record<string, unknown>)[column.key];

  let stringified: string;

  if (column.format) {
    const formatted = column.format(rawValue, row);
    stringified =
      formatted === null || formatted === undefined ? '' : formatted;
  } else {
    stringified = defaultStringify(rawValue);
  }

  if (opts.truncateLongCells) stringified = truncateForExcel(stringified);
  if (opts.formulaGuard) stringified = guardFormulaInjection(stringified);
  return csvEscape(stringified, opts.delimiter);
}

function defaultStringify(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) {
    // Default to date-only (YYYY-MM-DD) — the canonical CSV date format, sortable and
    // locale-independent. Callers who need the time component should use `formatIsoDateTime`.
    return Number.isNaN(value.getTime())
      ? ''
      : value.toISOString().slice(0, 10);
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return String(value);
}
